<template>
  <FormulateForm
    ref="configurationForm"
    name="configurationForm"
  >
    <e-sidebar
      id="sidebar-price-suggestion-configuration"
      :title="title"
      :show="showSidebar"
      :fetching="fetching"
      :saving="saving"
      width="500px"
      @save="saveConfiguration"
      @hidden="hideSidebar"
    >
      <template #content>
        <b-row>
          <b-col md="12">
            <e-store-combo
              id="price-suggestion-configuration-store"
              v-model="configurationForm.storeId"
              :required="false"
              :select-single-option="false"
              :placeholder="$t('Geral')"
              :instruction="$t('Deixe em branco para uma configuração geral')"
            />
          </b-col>
          <b-col md="12">
            <e-search-sku
              id="price-suggestion-configuration-sku"
              v-model="configurationForm.skuId"
              :label="$t('Produto')"
              :required="false"
              :only-active="true"
              :include-fixed-assets="true"
              :placeholder="$t('Geral')"
              :instruction="$t('Deixe em branco para um Geral de produto (todos os produtos)')"
              :default-options="skuDefaultOptions"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              id="price-suggestion-configuration-minimum-margin"
              v-model.number="configurationForm.minimumMarginPercent"
              type="number"
              :step="0.01"
              class="required"
              :label="$t('Margem Mínima (%)')"
              validation="required|number|min:0|max:999.99|maxTwoDecimals"
              :validation-rules="{ maxTwoDecimals }"
              :validation-messages="{
                maxTwoDecimals: $t('Informe no máximo duas casas decimais'),
              }"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              id="price-suggestion-configuration-maximum-variation"
              v-model.number="configurationForm.maximumVariationPercent"
              type="number"
              :step="0.01"
              class="required"
              :label="$t('Variação Máxima (%)')"
              validation="required|number|min:0|max:999.99|maxTwoDecimals"
              :validation-rules="{ maxTwoDecimals }"
              :validation-messages="{
                maxTwoDecimals: $t('Informe no máximo duas casas decimais'),
              }"
            />
          </b-col>
        </b-row>
      </template>
    </e-sidebar>
  </FormulateForm>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import { ESidebar, ESearchSku } from '@/views/components'
import { formulateHelper } from '@/mixins'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import { mapActions } from 'vuex'

const getInitialForm = () => ({
  id: null,
  storeId: null,
  skuId: null,
  minimumMarginPercent: null,
  maximumVariationPercent: null,
})

export default {
  components: {
    BRow,
    BCol,
    ESidebar,
    ESearchSku,
    EStoreCombo,
  },

  mixins: [formulateHelper],

  data() {
    return {
      showSidebar: false,
      fetching: false,
      saving: false,
      edit: false,
      configurationForm: getInitialForm(),
    }
  },

  computed: {
    title() {
      const entity = this.$tc('PRICE_SUGGESTION_CONFIGURATION.NAME', 1)
      return this.edit ? `${this.$t('Editar')} ${entity}` : `${this.$t('Novo')} ${entity}`
    },

    // Opção pré-selecionada do ESearchSku (busca sob demanda não pré-carrega opções). Derivada do
    // formulário para ficar sempre em sincronia com o valor selecionado, garantindo que o combo
    // exiba o produto na edição. Vazio quando Geral de produto (skuId nulo).
    skuDefaultOptions() {
      const { skuId, skuName } = this.configurationForm
      if (skuId == null) return []
      // id como string para casar com o value do combo (select-searchable compara com ===).
      return [{ id: String(skuId), name: skuName || `#${skuId}`, active: true }]
    },
  },

  methods: {
    ...mapActions('pages/catalog/priceSuggestionConfigurations', [
      'createConfiguration',
      'updateConfiguration',
    ]),

    // Espelha o decimal(5,2) do backend: no máximo duas casas decimais.
    maxTwoDecimals({ value }) {
      if (value === '' || value === null || value === undefined) return true
      return /^\d+(\.\d{1,2})?$/.test(String(value))
    },

    showCreate() {
      this.configurationForm = getInitialForm()
      this.$refs.configurationForm.hideErrors()
      this.edit = false
      this.showSidebar = true
    },

    show({ id, item } = {}) {
      if (!id && !item?.id) {
        this.showCreate()
      } else {
        this.configurationForm = {
          ...getInitialForm(),
          ...(item || {}),
          id: id || item?.id,
          // Os combos comparam as opções por string; converte os ids na edição.
          // Loja/Produto em branco = Geral (null).
          storeId: item?.storeId != null ? String(item.storeId) : null,
          skuId: item?.skuId != null ? String(item.skuId) : null,
        }
        this.$refs.configurationForm.hideErrors()
        this.edit = true
        this.showSidebar = true
      }
    },

    hideSidebar() {
      this.configurationForm = getInitialForm()
      this.showSidebar = false
    },

    async saveConfiguration() {
      this.$refs.configurationForm.showErrors()
      if (this.$refs.configurationForm.isValid) {
        try {
          this.saving = true

          // O `id` NÃO vai no corpo: no create ele não existe e no update é
          // enviado pela rota (`{Id:int}`). O backend desserializa o corpo como
          // `SkuPriceSuggestionConfigurationDto` (Id é `int` não-anulável), então
          // enviar `id: null` quebraria a conversão para System.Int32.
          const payload = {
            // Loja/Produto em branco ⇒ Geral (todas as lojas / todos os produtos): envia null.
            storeId: this.configurationForm.storeId || null,
            skuId: this.configurationForm.skuId || null,
            minimumMarginPercent: this.configurationForm.minimumMarginPercent,
            maximumVariationPercent: this.configurationForm.maximumVariationPercent,
          }

          if (this.edit) {
            await this.updateConfiguration({ id: this.configurationForm.id, payload })
          } else {
            await this.createConfiguration(payload)
          }

          this.showSuccess({
            message: this.$t(`${this.edit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
          })
          this.showSidebar = false
          this.$emit('save')
        } catch (error) {
          this.showError({ error })
        } finally {
          this.saving = false
        }
      } else {
        this.showInvalidDataMessage()
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
